import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import Step1Twilio from "./steps/Step1Twilio";
import Step2NumberContainer from "./steps/Step2NumberContainer";
import Step3Forwarding from "./steps/Step3Forwarding";
import Step4Recording from "./steps/Step4Recording";
import Step5DNI from "./steps/Step5DNI";
import { useOnboardingState } from "@/integrations/supabase/onboarding";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";

const stepTitles = [
  "Connect Twilio",
  "Buy First Number",
  "Add Forwarding Destination",
  "Recording & Retention Settings",
  "Copy DNI Snippet"
];

function OnboardingWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState(0);
  const { onboarding, loading, error, markComplete } = useOnboardingState();

  useEffect(() => {
    if (onboarding?.status === "completed") onClose();
  }, [onboarding, onClose]);

  useEffect(() => {
    if (onboarding?.current_step) {
      const stepIndex = stepTitles.findIndex(
        (_, idx) => ["twilio", "number", "forward", "recording", "dni"][idx] === onboarding.current_step
      );
      if (stepIndex !== -1) {
        setStep(stepIndex);
      }
    }
  }, [onboarding]);

  const next = () => setStep((s) => Math.min(4, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const steps = [
    <Step1Twilio key={0} onNext={next} />,
    <Step2NumberContainer key={1} onNext={next} onPrev={prev} />,
    <Step3Forwarding key={2} onNext={next} onPrev={prev} />,
    <Step4Recording key={3} onNext={next} onPrev={prev} />,
    <Step5DNI
      key={4}
      onPrev={prev}
      onFinish={async () => {
        await markComplete();
        onClose();
      }}
    />,
  ];

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={open ? onClose : undefined}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Loading Onboarding...
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 sm:p-6 space-y-4 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p>Loading your onboarding progress...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onOpenChange={open ? onClose : undefined}>
        <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center text-destructive">
              <AlertCircle className="h-5 w-5 mr-2" /> 
              Setup Error
            </DialogTitle>
            <DialogDescription>
              There was a problem loading your onboarding progress
            </DialogDescription>
          </DialogHeader>
          <div className="p-4 sm:p-6 space-y-4">
            <div className="bg-destructive/10 p-4 rounded-md text-destructive text-sm">
              {error}
            </div>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 mt-4">
              <Button variant="outline" onClick={onClose} className="min-h-[44px]">
                Close
              </Button>
              <Button onClick={() => window.location.reload()} className="min-h-[44px]">
                Try Again
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={open ? onClose : undefined}>
      <DialogContent className="max-w-3xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">
            Step {step + 1}/5 · {stepTitles[step]}
          </DialogTitle>
        </DialogHeader>
        <div className="p-4 sm:p-6 space-y-4">{steps[step]}</div>
      </DialogContent>
    </Dialog>
  );
}

export default OnboardingWizard;
